import React, { useEffect, useMemo, useState } from "react";
import { IMessage, useChatState } from "../../state/ChatState";
import { useScript } from "../../../hooks/useScript";
import { applyConfiguration } from "../../helpers/applyConfiguration";
import { ChooseAgent } from "./ChooseAgent";
import { LoadingSpinner } from "../LoadingSpinner";
import { Message } from "../Message";
import toast from "react-hot-toast";

export const DigitalFriendChatWS = () => {
	const chatState = useChatState(); // global state
	const loaded = useScript({ src: "/scripts/testAgentScript.js" });
	const messages = chatState.chatMessages;
	const queryParams = new URLSearchParams(location.search);
	const isIframe = queryParams.get("iframe") === "true";
	const sourceName = queryParams.get("sourceName") || "";
	const isVoiceChat = chatState.baseConfig?.IS_VOICE_CHAT == "true";
	const chatSuggestions = chatState.baseConfig?.CHAT_SUGGESTIONS?.split("|");
	const keyboardLabels = chatState.baseConfig?.KEYBOARD_LABELS?.split("|");
	const connectingMessages =
		chatState.baseConfig?.CONNECTING_MESSAGES?.split("|");
	const productChatLogoUrl =
		chatState.baseConfig?.PRODUCT_CHAT_LOGO_URL ||
		"https://symantopublic.blob.core.windows.net/logos/symanto.svg";
	// const showImages = chatState.baseConfig?.SHOW_IMAGES != "false";
	const showImages = false;
	const headerLogoUrl =
		chatState.baseConfig?.HEADER_LOGO_URL || productChatLogoUrl;

	const [initApproved, setInitApproved] = useState(false);
	const [isInitializing, setInitializing] = useState(true);
	const [languageIndex, setLanguageIndex] = useState(0);
	const [userMessage, setUserMessage] = useState<string>("");
	const [isChatEntryEnabled, setIsChatEntryEnabled] = useState(false);

	const chatSuggestionsByLanguage = useMemo(
		() => chatSuggestions?.[languageIndex]?.split(";"),
		[chatSuggestions, languageIndex],
	);

	applyConfiguration(
		String(chatState.baseConfig?.PRIMARY_COLOR),
		String(chatState.baseConfig?.SECONDARY_COLOR),
		String(chatState.baseConfig?.TITLE),
		chatState.baseConfig?.BACKGROUND_COLOR,
	);

	/**
	 * The `startCall` function initiates a conversation with a Symanto agent, processes incoming
	 * messages, updates the chat state, and sets the microphone as active.
	 * @param {string} agentId - The `agentId` parameter in the `startCall` function is a string that
	 * represents the identifier of the agent with whom the conversation will be started.
	 */
	const startCall = (agentId: string, source?: string) => {
		chatState.resetState();
		window.symantoAgentTester.startConversation(
			agentId,
			chatState.baseConfig!.API_KEY,
			chatState.baseConfig!.BASE_API_URL,
			true,
			undefined,
			source,
		);

		window.symantoAgentTester.messageChannel.port2.onmessage = (event) => {
			const { id, role, isComplete, content } = event.data;
			const _msg: IMessage = {
				id: id,
				conversationId: id,
				messageAuthor: role == "2" ? 0 : 1,
				text: content,
				finalMessage: isComplete,
				date: new Date().toISOString(),
			};
			chatState.updateMessage(_msg, true);
		};

		window.symantoAgentTester.eventChannel.port2.onmessage = handleEventMessage;
	};

	const handleEventMessage = (event: { data: { type: any; message: any } }) => {
		const { type, message } = event.data;
		if (type == "success") {
			toast.dismiss();
			toast.success(message);
			setIsChatEntryEnabled(true);
		}
		if (type == "error") {
			toast.error(message);
			setIsChatEntryEnabled(false);
		}
	};

	/**
	 * The `initChat` function initializes a chat with an agent using the provided agentId and optional
	 * promptId.
	 * @param {number} langIndex - The `languageIndex` parameter is a required number that represents the selected language from the choose agent view
	 * @param {string} agentId - The `agentId` parameter is a required string that represents the
	 * identifier of the agent for the chat initialization.
	 * @param {string} [promptId] - The `promptId` parameter in the `initChat` function is an optional
	 * parameter of type string. It is used to specify a prompt ID that can be passed to the `startCall`
	 * function when initiating a chat. If a `promptId` is provided, the `startCall` function
	 */
	const initChat = async (
		langIndex: number,
		agentId: string,
		promptId?: string,
	) => {
		setInitializing(false);
		setInitApproved(true);
		setLanguageIndex(langIndex);
		let _agent = agentId;
		if (promptId && promptId !== "undefined") {
			_agent = promptId;
		}

		setIsChatEntryEnabled(true);
		let source = sourceName?.trim() ?? "";

		if (source == "") {
			if (document.referrer !== "") {
				source = document.referrer;
			} else {
				source = window.location.host;
			}
		}

		startCall(_agent, source);
	};

	const sendMessage = (event: React.FormEvent) => {
		event.preventDefault();

		window.symantoAgentTester.sendTextMessage(userMessage);

		setUserMessage("");
	};

	const onSuggestionClick = (suggestion: string) => {
		window.symantoAgentTester.sendTextMessage(`${suggestion}`);
	};

	const selectDefaultAgent = () => {
		const agentId = String(chatState.baseConfig?.AGENT_IDS.split("|")?.[0]);
		const promptId = chatState.baseConfig?.PROMPT_ID;
		initChat(0, agentId, promptId);
	};

	useEffect(() => {
		if (loaded && isIframe && isInitializing) {
			selectDefaultAgent();
		}
	}, [loaded]);

	return (
		<div
			className="min-h-[100vh] max-h-[100vh] flex overflow-y-auto"
			style={{ backgroundColor: "#ebebef" }}
		>
			{loaded === false && (
				<div className="flex flex-1 items-center justify-center">
					<LoadingSpinner />
				</div>
			)}

			{loaded === true && initApproved === false && !isIframe && (
				<div className="flex flex-1 flex-col items-center p-4 overflow-y-auto w-full">
					<div
						data-id="app_header"
						className="w-full flex justify-center pt-6 md:pt-10"
					>
						{!!headerLogoUrl && showImages && (
							<img
								src={headerLogoUrl}
								className="h-[54px] max-h-[54px] md:h-[68px] md:max-h-[68px] object-contain"
								alt="Logo"
							/>
						)}
					</div>

					<div
						data-id="app_middle"
						className="flex flex-1 flex-col items-center justify-center w-full"
					>
						<ChooseAgent selectedAgent={initChat} />
					</div>

					<div
						data-id="app_bottom"
						className="w-full flex flex-col items-center pb-6 md:pb-10"
					>
						{showImages && (
							<img
								src="/powered-by-symanto-logo.svg"
								className="h-[32px] max-h-[32px] md:h-[45px] md:max-h-[45px] object-contain opacity-90"
								alt="Logo"
							/>
						)}
					</div>
				</div>
			)}

			{loaded === true && (initApproved || isIframe) && (
				<div
					className={`flex flex-1 flex-col items-center overflow-y-auto ${
						!isIframe ? "p-8" : " bg-white mt-4"
					}`}
				>
					{initApproved && (
						<div className="w-full max-w-[900px] flex flex-col flex-1 overflow-y-auto ">
							{(isInitializing || messages.length == 0) && (
								<div
									className={`flex w-full mt-1 md:mt-4 gap-3 items-center ${
										isIframe ? "p-4" : "p-0"
									}`}
								>
									{!!productChatLogoUrl && !isIframe && showImages && (
										<img
											src={productChatLogoUrl}
											className="w-full max-w-[50px] max-h-[50px] md:max-w-[60px] md:max-h-[60px] object-contain"
										/>
									)}
									<p className="text-sm md:text-base">
										{connectingMessages?.[languageIndex]}
									</p>
								</div>
							)}

							{!isInitializing && (
								<>
									<div
										id="chat"
										className={`flex flex-1 ${
											isIframe
												? "flex-col-reverse"
												: "flex-col p-4 gap-2 md:gap-6"
										}  overflow-y-auto`}
									>
										{(isIframe ? [...messages].reverse() : messages)?.map(
											(message, index) => {
												return (
													<Message
														key={index}
														message={message}
														isIframe={isIframe}
													/>
												);
											},
										)}

										{messages?.length === 1 &&
											!!chatSuggestionsByLanguage?.length && (
												<div className="flex flex-1 items-end justify-center">
													<div className="w-full flex md:px-[60px]">
														<div className="w-full md:px-3 flex flex-col">
															<div className="w-full flex items-center gap-2 md:gap-4">
																{chatSuggestionsByLanguage.map(
																	(suggestion, index) => (
																		<div
																			key={index}
																			className="flex flex-col flex-1 border border-gray-200 rounded-lg h-full p-2 md:p-4 cursor-pointer hover:bg-white hover:shadow-md"
																			onClick={() =>
																				onSuggestionClick(suggestion)
																			}
																		>
																			{showImages && (
																				<img
																					src="core-lightbulb.svg"
																					className="w-[18px] h-[18px] md:w-[24px] md:h-[24px]"
																				/>
																			)}
																			<p className="mt-2 word-break-all text-sm md:text-base">
																				{suggestion}
																			</p>
																		</div>
																	),
																)}
															</div>
														</div>
													</div>
												</div>
											)}
									</div>
									{!isVoiceChat && (
										<div
											className={`flex w-full  ${
												!isIframe ? "p-0.5 mt-3 md:mt-5" : "p-0"
											}`}
										>
											<div
												className={`w-full flex ${
													!isIframe ? "md:px-[60px]" : ""
												}`}
											>
												<div
													className={`w-full ${
														!isIframe ? "md:px-3" : ""
													} flex flex-col`}
												>
													<form onSubmit={(event) => sendMessage(event)}>
														<div
															className={`flex flex-1 relative w-full ${
																isIframe ? "mb-0" : ""
															}`}
														>
															<input
																type="text"
																className={`block w-full text-base md:text-base text-gray-900 placeholder-gray-custom disabled:cursor-not-allowed ${
																	isIframe
																		? "bg-transparent pl-3 pr-14 md:pl-4 md:pr-20 py-3 md:py-5 pr-14 border-t-[1px] border-gray-custom rounded-none shadow-none focus:outline-none focus:ring-0"
																		: "p-3 md:p-5 rounded-lg bg-white shadow-sm focus:primary pr-14 md:pr-20"
																}`}
																placeholder={
																	keyboardLabels?.[languageIndex] ?? "Message"
																}
																disabled={!isChatEntryEnabled}
																value={userMessage}
																onChange={(e) => setUserMessage(e.target.value)}
																autoComplete="off"
																required
															/>

															{isIframe ? (
																<button
																	type="submit"
																	className="absolute end-1 bottom-[6px] md:end-2 md:bottom-2.5 px-2 py-1 md:px-3 md:py-2 rounded-lg disabled:opacity-20 disabled:pointer-events-none disabled:cursor-not-allowed"
																	disabled={
																		!userMessage?.length || !isChatEntryEnabled
																	}
																>
																	<svg
																		className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]"
																		fill={chatState.baseConfig?.PRIMARY_COLOR}
																		viewBox="0 0 1024 1024"
																	>
																		<path d="M85.76 896l895.573333-384-895.573333-384-0.426667 298.666667 640 85.333333-640 85.333333z" />
																	</svg>
																</button>
															) : (
																<button
																	type="submit"
																	disabled={
																		!userMessage?.length || !isChatEntryEnabled
																	}
																	className="absolute end-1 bottom-[6px] md:end-2 md:bottom-2.5 px-2 py-1 md:px-3 md:py-2 rounded-lg bg-transparent hover:shadow-xl disabled:opacity-20 disabled:pointer-events-none disabled:cursor-not-allowed"
																>
																	<img src="/ic_send.svg" />
																</button>
															)}
														</div>
													</form>
												</div>
											</div>
										</div>
									)}
								</>
							)}
						</div>
					)}

					<div>
						{!!productChatLogoUrl && showImages && initApproved === false && (
							<img
								src={productChatLogoUrl}
								className="h-full min-h-[40px] max-h-[40px] md:min-h-[60px] md:max-h-[60px] object-contain mb-2"
							/>
						)}
					</div>

					{initApproved === false && <ChooseAgent selectedAgent={initChat} />}

					{!isIframe ? (
						<div className="flex items-center justify-center mt-3 md:mt-6">
							{showImages && (
								<img
									src="/powered-by-symanto-logo.svg"
									className="max-h-[26px] md:max-h-[60px]"
									alt="Logo"
								/>
							)}
						</div>
					) : (
						<div className="pb-2 text-xs text-gray-custom text-center">
							Powered by{" "}
							<a
								href="https://www.symanto.com/ai-contact-centre"
								target="_blank"
								rel="noopener noreferrer"
								className="font-semibold hover:underline"
								style={{ color: chatState.baseConfig?.PRIMARY_COLOR }}
							>
								Symanto
							</a>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
